"use client"

import { useEffect } from "react"
import { usePathname } from "next/navigation"
import { readStoredUser } from "@/lib/safe-storage"
import { addBreadcrumb, labelFor } from "@/lib/errors/breadcrumbs"
import { sendClientError } from "@/lib/errors/client-report"

// Module-scope guards survive React remounts so a render loop can't reset them.
let sentCount = 0
const MAX_PER_LOAD = 10            // hard cap per page load
const recent = new Map<string, number>() // client signature -> last-sent ms
const DEDUP_WINDOW_MS = 30_000

/** Stable-ish description for any thrown value (Error, string, or object). */
function describe(reason: unknown): { message: string; stack?: string } {
  if (reason instanceof Error) return { message: reason.message, stack: reason.stack }
  if (typeof reason === "string") return { message: reason }
  try { return { message: JSON.stringify(reason)?.slice(0, 500) || "Unknown error" } }
  catch { return { message: String(reason) } }
}

export default function ErrorReporter() {
  const pathname = usePathname()

  // Page changes, so a report can say which pages the visitor went through.
  useEffect(() => {
    if (pathname) addBreadcrumb("nav", pathname)
  }, [pathname])

  useEffect(() => {
    // Identify the user in Sentry by id ONLY: email/name are PII and are not
    // needed to attribute a stack trace (they're also stored separately, in our
    // own EU DB, by the error-report API derived from the verified session).
    try {
      const u = readStoredUser()
      if (u.id) {
        import("@sentry/nextjs").then(S => S.setUser({ id: u.id })).catch(() => {})
      }
    } catch { /* ignore */ }

    function report(message: string, stack?: string) {
      if (!message || message === "Script error.") return

      // Cloudflare Turnstile widget errors (e.g. "TurnstileError ... 600010") are
      // third-party/environmental: domain allow-list, automated/uncommon browsers,
      // transient network. Not actionable app bugs.
      if (/turnstile/i.test(message)) return

      // Per-load cap + short-window dedup so a tight error loop can't flood the
      // endpoint. Signature mirrors the server's (message[:200] + "|" + path).
      if (sentCount >= MAX_PER_LOAD) return
      const sig = message.slice(0, 200) + "|" + window.location.pathname
      const now = Date.now()
      const last = recent.get(sig)
      if (last && now - last < DEDUP_WINDOW_MS) return
      recent.set(sig, now)
      if (recent.size > 50) {
        for (const [k, t] of recent) { if (now - t > DEDUP_WINDOW_MS) recent.delete(k) }
      }
      sentCount++

      // Extension-only stacks are dropped inside sendClientError.
      void sendClientError({ message, stack })
    }

    // What they clicked. Capture phase so it is recorded even if a handler
    // throws or stops propagation.
    function onClick(event: MouseEvent) {
      try {
        const label = labelFor(event.target)
        if (label) addBreadcrumb("click", label)
      } catch { /* never interfere with the click */ }
    }

    function onError(event: ErrorEvent) {
      if (!event.message || event.message === "Script error.") return
      report(event.message, event.error?.stack)
    }

    function onUnhandledRejection(event: PromiseRejectionEvent) {
      const { message, stack } = describe(event.reason)
      report(message || "Unhandled promise rejection", stack)
    }

    document.addEventListener("click", onClick, true)
    window.addEventListener("error", onError)
    window.addEventListener("unhandledrejection", onUnhandledRejection)
    return () => {
      document.removeEventListener("click", onClick, true)
      window.removeEventListener("error", onError)
      window.removeEventListener("unhandledrejection", onUnhandledRejection)
    }
  }, [])

  return null
}
